<template>
  <article ref="chartRoot" class="hezabat-report-panel hezabat-report-panel--status">
    <header class="hezabat-report-panel__head">
      <div>
        <h2 class="hezabat-report-panel__title">{{ $t("reportChartByStatus") }}</h2>
      </div>
    </header>

    <div ref="canvasHost" class="hezabat-report-panel__chart-host hezabat-report-panel__chart-host--donut">
      <div v-if="hasChartData" class="hezabat-report-donut">
        <div class="hezabat-report-donut__canvas">
          <canvas ref="statusChart" class="hezabat-report-panel__canvas" />
        </div>
        <ul class="hezabat-report-donut__legend">
          <li v-for="item in visibleByStatus" :key="item.statusKey" class="hezabat-report-donut__item">
            <span class="hezabat-report-donut__dot" :style="{ background: statusColor(item.statusKey) }" />
            <span class="hezabat-report-donut__text">
              <span class="hezabat-report-donut__name">{{ statusLabel(item) }}</span>
              <span class="hezabat-report-donut__meta">
                {{ item.count }} · {{ item.shareOfTotal }}%
              </span>
            </span>
          </li>
        </ul>
      </div>
      <p v-else class="hezabat-report-panel__empty">{{ $t("reportChartNoData") }}</p>
    </div>
  </article>
</template>

<script>
import Chart from "chart.js/auto";
import { POLICY_STATUS, policyStatusLabelFromId } from "./agentRequestsApi.js";

const STATUS_COLORS = {
  [POLICY_STATUS.NOT_SENT]: "#94a3b8",
  [POLICY_STATUS.SENT_FOR_PAYMENT]: "#818cf8",
  [POLICY_STATUS.PAID]: "#34d399",
  [POLICY_STATUS.DELETED]: "#fbbf24",
  [POLICY_STATUS.REJECTED]: "#fb7185",
};

export default {
  name: "ReportStatusChart",
  props: {
    byStatus: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chartInstance: null,
      resizeObserver: null,
      renderRetry: 0,
    };
  },
  computed: {
    visibleByStatus() {
      return this.byStatus.filter((item) => Number(item.count || 0) > 0);
    },
    hasChartData() {
      return this.visibleByStatus.length > 0;
    },
    statusPeriodTotal() {
      return this.visibleByStatus.reduce((sum, item) => sum + Number(item.count || 0), 0);
    },
  },
  watch: {
    byStatus: {
      deep: true,
      handler() {
        this.$nextTick(() => this.scheduleRender());
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.scheduleRender();
      this.bindResizeObserver();
    });
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.destroyChart();
  },
  methods: {
    statusColor(statusKey) {
      return STATUS_COLORS[statusKey] || "#94a3b8";
    },
    statusLabel(item) {
      const name = item?.statusName != null ? String(item.statusName).trim() : "";
      if (name) return name;
      return policyStatusLabelFromId(item?.statusId) || item?.statusKey || "";
    },
    scheduleRender() {
      this.renderRetry = 0;
      this.renderChart();
    },
    bindResizeObserver() {
      const host = this.$refs.canvasHost;
      if (!host || typeof ResizeObserver === "undefined") return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.chartInstance) {
          this.chartInstance.resize();
          return;
        }
        this.scheduleRender();
      });
      this.resizeObserver.observe(host);
    },
    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
    },
    renderChart() {
      if (!this.hasChartData) {
        this.destroyChart();
        return;
      }

      const canvas = this.$refs.statusChart;
      const host = this.$refs.canvasHost;
      if (!canvas || !host) return;

      const { width, height } = host.getBoundingClientRect();
      if ((width < 8 || height < 8) && this.renderRetry < 12) {
        this.renderRetry += 1;
        requestAnimationFrame(() => this.renderChart());
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      this.destroyChart();

      const labels = this.visibleByStatus.map((item) => this.statusLabel(item));
      const data = this.visibleByStatus.map((item) => Number(item.count || 0));
      const colors = this.visibleByStatus.map((item) => this.statusColor(item.statusKey));

      this.chartInstance = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data,
              backgroundColor: colors,
              borderColor: "rgba(15, 23, 42, 0.85)",
              borderWidth: 3,
              spacing: 2,
              hoverOffset: 10,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "52%",
          animation: { duration: 480 },
          plugins: {
            legend: { display: false },
          },
        },
      });
    },
  },
};
</script>
